import { debounce } from "lodash";
import { DebouncedFunc } from "lodash"; // Not declared in @types/lodash.debounce for some reason
import { Point } from "openseadragon";

import { ExtendedSeadragonViewer } from "./ExtendedSeadragon";

// https://github.com/openseadragon/bookmark-url/blob/master/openseadragon-bookmark-url.js
export const parseHash: (hashString?: string) => any = (
  hashString = window.location.hash
) => {
  let params: any = {};
  const hash = hashString.replace(/^#/, "");
  if (hash) {
    const parts = hash.split("&");
    parts.forEach((part) => {
      const subparts = part.split("=");
      const key = subparts[0];
      const value = parseFloat(subparts[1]);
      if (!key || isNaN(value)) {
        console.error("bad hash param", part);
      } else {
        params[key] = value;
      }
    });
  }

  return params;
};

// Given the slide_id, returns a possibly cached ZoomParams, or a parsed window.location.hash
export const parseCachedHash = (slide_id: number) => {
  return parseHash(hashParamsBySlide.get(slide_id));
};

function toHashParams(
  zoom: number,
  rot: number,
  pan: { x: number; y: number }
) {
  let params = "";
  if (zoom) {
    params += "&zoom=" + Math.round(4 * zoom) / 4; // handle 0.5 and 0.25 cases correctly
  }
  if (rot % 360) {
    params += "&rot=" + Math.round(rot % 360);
  }
  if (pan.x) {
    params += "&x=" + Math.round(10000 * pan.x) / 10000;
  }
  if (pan.y) {
    params += "&y=" + Math.round(10000 * pan.y) / 10000;
  }
  if (params.length > 0) {
    params = "#" + params.substring(1);
  }
  return params;
}

const viewportParameters = (
  params: any,
  viewer: ExtendedSeadragonViewer | null
) => {
  if (!viewer) return;
  const rot = viewer.viewport.getRotation();
  const pan = viewer.viewport.getCenter();

  if (params.zoom !== undefined && !Number.isNaN(Number(params.zoom))) {
    viewer.zoomToEffectiveMagnification(Number(params.zoom));
  }

  if (params.rot !== undefined && params.rot !== rot) {
    // @ts-expect-error: latest @types for OSD does not include "immediately" paramter to setRotation
    viewer.viewport.setRotation(params.rot, true);
  }

  if (
    params.x !== undefined &&
    params.y !== undefined &&
    (params.x !== pan.x || params.y !== pan.y)
  ) {
    viewer.viewport.panTo(new Point(params.x, params.y), true);
  }
};

let currentViewer: ExtendedSeadragonViewer | null = null;
let updateUrl: DebouncedFunc<() => void> | undefined = undefined;
let hashParamsBySlide = new Map<number, string>();
let onHashChange = () => {
  viewportParameters(parseHash(), currentViewer);
};

export const bookmarkUrl = (
  viewer: ExtendedSeadragonViewer,
  slide_id: number
) => {
  currentViewer = null; // TODO: remove this entirely in here   // figure out what functionality it was providing
  // Zoom, pan, and rotate listener, debounced:
  updateUrl = debounce(() => {
    const loc = window.location;
    const zoom = viewer.getZoomForBookmark();
    const rot = viewer.viewport.getRotation();
    const pan = viewer.viewport.getCenter();
    const oldUrl = loc.pathname + loc.hash;
    const hashParams = toHashParams(zoom, rot, pan);
    hashParamsBySlide.set(slide_id, hashParams);
    const url = loc.pathname + hashParams;
    window.history.replaceState({}, "", url);

    if (url !== oldUrl) {
      viewer.raiseEvent("bookmark-url-change", { url: window.location.href });
    }
  }, 100);

  const params = parseCachedHash(slide_id);

  if (viewer.world.getItemCount() === 0) {
    // console.log('viewer world item count 0')
    viewer.addOnceHandler("open", () => {
      viewportParameters(params, viewer);
    });
  } else {
    // console.log('else')
    viewportParameters(params, viewer);
  }

  viewer.addHandler("zoom", updateUrl);
  viewer.addHandler("pan", updateUrl);
  viewer.addHandler("rotate", updateUrl);

  // Note that out own replaceState calls don't trigger hashchange events, so this is only if
  // the user has modified the URL (by pasting one in, for instance).
  window.addEventListener("hashchange", onHashChange, false);
};

// Clears viewer hash parameters (zoom, pan, rotate) from the URL after
// navigating way from the viewer or navigating through cases
export const cleanUrl = () => {
  window.removeEventListener("hashchange", onHashChange, false);

  // Ensure no further calls to updateUrl debounce payload
  if (updateUrl) {
    currentViewer?.removeHandler("zoom", updateUrl);
    currentViewer?.removeHandler("pan", updateUrl);
    currentViewer?.removeHandler("rotate", updateUrl);
    updateUrl?.cancel(); // cancel any pending debounce
    updateUrl = undefined;
  }
  currentViewer?.destroy();
  currentViewer = null;

  const loc = window.location;
  if (loc.hash) {
    window.history.replaceState({}, "", loc.pathname);
  }
};
